import { LayoutService } from '../../../../shared/services/layout.service';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { GridsterConfig, GridType } from 'angular-gridster2';
import { take } from 'rxjs/operators';
import { Layout } from '../../../../shared/models/layout';
import { Subject } from '../../../../shared/models/subject';
import { WidgetService } from '../../../widgets/widget.service';
import { Grid } from 'app/shared/helpers/grid';

@Component({
  selector: 'subject-card',
  templateUrl: './subject-card.component.html',
  styleUrls: ['./subject-card.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SubjectCardComponent implements OnInit {
  _subject: Subject;

  layout: Layout;
  grid = new Grid();
  options: GridsterConfig;
  gridSize = 30;
  style: any;
  _autoSize = true;

  @Input() set subject(val: Subject) {
    this._subject = new Subject(val);
    this.updateLayout(this._subject);
  }
  get subject() {
    return this._subject;
  }

  @Input() set autoSize(val: boolean) {
    const changed = val !== this._autoSize;
    this._autoSize = val;
    if (changed && this._subject) {
      this.updateLayout(this._subject);
    }
  }

  // size is set by the template, make size accessible to encapsulating components
  @Input() sizeX = 100;
  @Input() sizeY = 100;

  @Output() onLayoutChanged: EventEmitter<any> = new EventEmitter();

  constructor(
    private layoutService: LayoutService,
    private widgetService: WidgetService
  ) {}

  ngOnInit() {
    // set gridster options
    this.options = {
      gridType: GridType.Fixed,
      fixedColWidth: this.gridSize,
      fixedRowHeight: this.gridSize,
      mobileBreakpoint: this.gridSize * 10,
      margin: 0,
      minCols: 1,
      maxCols: 50,
      minRows: 1,
      maxRows: 100,
      draggable: {
        enabled: false,
      },
      resizable: {
        enabled: false,
      },
    };
  }

  updateLayout(subject: Subject, subjectTypeId?: number) {
    if (subject) {
      const categoryName = 'categoryid';
      // get the template for the subject card
      this.layoutService
        .getLayout(subject.subjectTypeId || subjectTypeId, subject.categoryId)
        .pipe(take(1))
        .subscribe((response: any) => {
          this.layout = response;

          // force the layout to be updated with any changes to the subject type, such as colour
          this.layout.subjectType = undefined;
          this.layoutService
            .hydrate(this.layout)
            .pipe(take(1))
            .subscribe((hydratedLayout) => {
              this.layout = hydratedLayout;
              const template =
                subject.isReferral && this.layout.referralCardTemplate.id
                  ? this.layout.referralCardTemplate
                  : this.layout.cardTemplate;

              // get the layoutitems from the card template
              const options = template.getSizeAndOptions(this.gridSize);
              const layoutChanged =
                this.sizeX !== options.sizeX ||
                this.sizeY !== options.sizeY + 25;
              this.sizeX = options.sizeX;
              this.sizeY = options.sizeY + 25;
              this.options = options.options;

              if (layoutChanged) {
                this.onLayoutChanged.emit({
                  layout: this.layout,
                  sizeX: this.sizeX,
                  sizeY: this.sizeY,
                });
              }

              // setup category info
              const categoryItem = template.layoutItems.find(
                (i) => i.name.toLowerCase() === categoryName
              );
              const categoryField =
                this.layout.subjectType.getDataField(categoryName);
              const categoryInfo = {
                name: categoryName,
                required: categoryField && categoryField.required,
                item: categoryItem,
                assigned: this._subject.categoryId,
              };

              this.grid.items = [];
              this.grid.addItems(
                this.subject,
                this.widgetService,
                template,
                this.layout.subjectType,
                categoryInfo
              );
              this.grid.values = this._subject.getMetadata(this.layout);

              // re-size
              if (this._autoSize) {
                this.style = {
                  'height.px': this.sizeY,
                  'width.px': this.sizeX,
                };
              } else {
                this.style = { 'height.px': '300' };
              }
            });
        });
    }
  }

  // MRD - Don't add trackbyFn as this will stop layout updating properly when category is changed
  // trackByFn(index, item){
  //   return item.id;
  // }
}
